import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { CompareProvider } from "@/context/CompareContext";
import { WishlistProvider } from "@/context/WishlistContext";
import SmoothScroll from "@/components/SmoothScroll";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ChatWidget from "@/components/ChatWidget";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Compare from "@/pages/Compare";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import BuyerAccount from "@/pages/BuyerAccount";
import SellOnHGK from "@/pages/SellOnHGK";
import SellerDashboard from "@/pages/SellerDashboard";
import SellerProfile from "@/pages/SellerProfile";
import FestivalStorefront from "@/pages/FestivalStorefront";
import SharedWishlist from "@/pages/SharedWishlist";
import AdminDashboard from "@/pages/AdminDashboard";

function RedirectTo({ to }) { return <Navigate to={to} replace />; }
function CategoryLanding() { const { a, b } = useParams(); return <Navigate to={`/shop?category=${b || a}`} replace />; }
function OccasionLanding() { const { slug } = useParams(); return <Navigate to={`/shop?occasion=${slug}`} replace />; }

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function Protected({ children, seller, admin }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen grid place-items-center text-mute">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/" replace />;
  if (seller && user.role !== "seller" && user.role !== "admin") return <Navigate to="/sell" replace />;
  return children;
}

function Shell() {
  const { pathname } = useLocation();
  const hideChrome = pathname === "/login" || pathname === "/register";
  return (
    <div className="grain min-h-screen flex flex-col">
      {!hideChrome && <Navbar />}
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/category/:a" element={<CategoryLanding />} />
          <Route path="/category/:a/:b" element={<CategoryLanding />} />
          <Route path="/occasion/:slug" element={<OccasionLanding />} />
          <Route path="/admin" element={<Protected admin><AdminDashboard /></Protected>} />
          <Route path="/festival/:slug" element={<FestivalStorefront />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/seller/:id" element={<SellerProfile />} />
          <Route path="/wishlist/:ownerId" element={<SharedWishlist />} />
          <Route path="/compare" element={<Compare />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Protected><Checkout /></Protected>} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/account" element={<Protected><BuyerAccount /></Protected>} />
          <Route path="/sell" element={<SellOnHGK />} />
          <Route path="/seller" element={<Protected seller><SellerDashboard /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {!hideChrome && <Footer />}
      {!hideChrome && <ChatWidget />}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <CompareProvider>
            <WishlistProvider>
              <SmoothScroll />
              <ScrollTop />
              <Toaster position="top-center" theme="light" richColors />
              <Shell />
            </WishlistProvider>
          </CompareProvider>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
